import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { remapProductImageUrl } from '../lib/api';
import { useCart } from '../context/CartContext';
import { Minus, Plus, X, ShoppingBag, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import axios from 'axios';
import { API } from '../lib/api';

const CartPage = () => {
    const { items, updateQuantity, removeItem, clearCart, total } = useCart();
    const [isCheckingOut, setIsCheckingOut] = React.useState(false);
    const [promoCode, setPromoCode] = React.useState('');
    const [promoApplied, setPromoApplied] = React.useState(null);
    const [promoError, setPromoError] = React.useState('');
    const [applyingPromo, setApplyingPromo] = React.useState(false);
    const [shippingSettings, setShippingSettings] = React.useState(null);
    const [fulfillmentMethod, setFulfillmentMethod] = React.useState('ship');

    const handleApplyPromo = async () => {
        if (!promoCode.trim()) return;
        setApplyingPromo(true);
        setPromoError('');
        try {
            const response = await axios.post(`${API}/promo-codes/validate`, {
                code: promoCode.trim(),
                order_total: total,
                product_ids: items.map(i => i.product.id),
                is_subscription: false
            });
            setPromoApplied(response.data);
        } catch (error) {
            setPromoError(error.response?.data?.detail || 'Invalid promo code');
            setPromoApplied(null);
        } finally {
            setApplyingPromo(false);
        }
    };

    const removePromo = () => {
        setPromoApplied(null);
        setPromoCode('');
        setPromoError('');
    };

    const discountedTotal = promoApplied ? Math.max(0, total - promoApplied.discount_amount) : total;

    const itemShippingTotal = React.useMemo(() => {
        return items.reduce((sum, item) => {
            const perUnit = Number(item.product?.shipping_cost);
            return item.product?.shipping_cost != null && Number.isFinite(perUnit) ? sum + Math.max(0, perUnit) * item.quantity : sum;
        }, 0);
    }, [items]);

    const hasProductShipping = React.useMemo(() => {
        return items.some(item => item.product?.shipping_cost != null && Number.isFinite(Number(item.product.shipping_cost)));
    }, [items]);

    React.useEffect(() => {
        const fetchShippingSettings = async () => {
            try {
                const response = await axios.get(`${API}/shipping-settings/public`);
                setShippingSettings(response.data);
            } catch (error) {
                console.error('Failed to load shipping settings', error);
            }
        };
        fetchShippingSettings();
    }, []);

    const shippingAmount = React.useMemo(() => {
        if (fulfillmentMethod === 'local_pickup') return 0;
        if (!shippingSettings || !shippingSettings.enabled || shippingSettings.mode === 'free') return 0;
        if (shippingSettings.mode === 'threshold' && Number(shippingSettings.free_over || 0) > 0 && discountedTotal >= Number(shippingSettings.free_over)) return 0;
        if (shippingSettings.mode === 'product_shipping' && hasProductShipping) return itemShippingTotal;
        return Number(shippingSettings.flat_rate || 0);
    }, [shippingSettings, discountedTotal, itemShippingTotal, hasProductShipping, fulfillmentMethod]);

    const displayTotal = discountedTotal + shippingAmount;

    const shippingPreviewText = React.useMemo(() => {
        if (fulfillmentMethod === 'local_pickup') return 'Local pickup: FREE';
        if (shippingSettings?.mode === 'product_shipping' && hasProductShipping) return shippingAmount > 0 ? `${shippingSettings?.label || 'Product Shipping'}: $${shippingAmount.toFixed(2)}` : 'Product shipping: FREE';
        if (!shippingSettings) return 'Calculated at checkout';
        if (!shippingSettings.enabled || shippingSettings.mode === 'free') return shippingSettings.free_label || 'Free shipping';
        if (shippingSettings.mode === 'threshold' && Number(shippingSettings.free_over || 0) > 0) {
            if (discountedTotal >= Number(shippingSettings.free_over)) return shippingSettings.free_label || 'Free shipping';
            return `${shippingSettings.label || 'Shipping'}: $${shippingAmount.toFixed(2)} or free over $${Number(shippingSettings.free_over).toFixed(2)}`;
        }
        return `${shippingSettings.label || 'Shipping'}: $${shippingAmount.toFixed(2)}`;
    }, [shippingSettings, discountedTotal, shippingAmount, hasProductShipping, fulfillmentMethod]);

    const handleCheckout = async () => {
        if (items.length === 0) return;
        
        setIsCheckingOut(true);
        try {
            const response = await axios.post(`${API}/checkout/create-session`, {
                items: items.map(item => ({
                    product_id: item.product.id,
                    quantity: item.quantity,
                    selected_grade: item.selected_grade || null
                })),
                origin_url: window.location.origin,
                promo_code: promoApplied?.code || null,
                fulfillment_method: fulfillmentMethod
            });
            
            // Redirect to Stripe checkout
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Checkout error:', error);
            const msg = error.response?.data?.detail || 'Failed to start checkout. Please try again.';
            alert(msg);
        } finally {
            setIsCheckingOut(false);
        }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="cart-page">
            <SEO
                title="Shopping Cart"
                description="Review your Wayside Maple cart. Premium maple syrup products ready for checkout with secure Stripe payment processing."
                path="/cart"
            />
            <Header />
            
            {/* Hero */}
            <section className="pt-32 pb-16 border-b border-black/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <span className="text-maple-red font-mono text-sm tracking-widest">
                        YOUR ORDER
                    </span>
                    <h1 className="font-anton text-5xl sm:text-6xl lg:text-7xl text-maple-dark mt-4">
                        CART
                    </h1>
                </div>
            </section>

            {/* Cart Content */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    {items.length === 0 ? (
                        <div className="text-center py-20 border border-black/10">
                            <ShoppingBag className="w-16 h-16 text-black/20 mx-auto mb-6" />
                            <p className="text-maple-dark/40 font-mono text-lg mb-6">Your cart is empty</p>
                            <Link to="/shop" className="btn-primary inline-block">
                                <span>Start Shopping</span>
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                            {/* Cart Items */}
                            <div className="lg:col-span-2 space-y-6">
                                {items.map(item => (
                                    <div 
                                        key={`${item.product.id}-${item.selected_grade || 'default'}`}
                                        className="flex gap-6 bg-maple-secondary border border-black/10 p-4 md:p-6"
                                        data-testid={`cart-item-${item.product.id}`}
                                    >
                                        {/* Image */}
                                        <div className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 bg-white overflow-hidden">
                                            {item.product.images && item.product.images.length > 0 ? (
                                                <img 
                                                    src={remapProductImageUrl(item.product.images[0])} 
                                                    alt={item.product.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-black/20">
                                                    <span className="font-mono text-xs">No Image</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="flex-1 flex flex-col justify-between">
                                            <div>
                                                <span className="text-maple-dark/40 text-xs font-mono uppercase">
                                                    {item.product.category}
                                                </span>
                                                <h3 className="font-anton text-lg md:text-xl text-maple-dark mt-1">
                                                    {item.product.name}
                                                </h3>
                                                <p className="font-mono text-maple-dark/60 mt-1">
                                                    ${item.product.price.toFixed(2)}
                                                </p>
                                                {item.selected_grade && (
                                                    <p className="font-mono text-maple-red text-xs mt-1">Grade: {item.selected_grade}</p>
                                                )}
                                            </div>

                                            {/* Quantity Controls */}
                                            <div className="flex items-center justify-between mt-4">
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.selected_grade || null)}
                                                        className="w-8 h-8 bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors text-maple-dark"
                                                        data-testid={`decrease-${item.product.id}`}
                                                    >
                                                        <Minus className="w-4 h-4" />
                                                    </button>
                                                    <span className="font-mono w-8 text-center text-maple-dark">{item.quantity}</span>
                                                    <button
                                                        onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.selected_grade || null)}
                                                        className="w-8 h-8 bg-black/5 hover:bg-black/10 flex items-center justify-center transition-colors text-maple-dark"
                                                        data-testid={`increase-${item.product.id}`}
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                <button
                                                    onClick={() => removeItem(item.product.id, item.selected_grade || null)}
                                                    className="p-2 text-maple-dark/40 hover:text-maple-red transition-colors"
                                                    data-testid={`remove-${item.product.id}`}
                                                >
                                                    <X className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Line Total */}
                                        <div className="hidden md:flex flex-col justify-center">
                                            <span className="font-mono text-lg text-maple-dark">
                                                ${(item.product.price * item.quantity).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                ))}

                                <button
                                    onClick={clearCart}
                                    className="text-maple-dark/40 hover:text-maple-red text-sm transition-colors"
                                    data-testid="clear-cart"
                                >
                                    Clear Cart
                                </button>
                            </div>

                            {/* Order Summary */}
                            <div className="lg:col-span-1">
                                <div className="bg-maple-secondary border border-black/10 p-6 md:p-8 sticky top-28">
                                    <h2 className="font-anton text-2xl text-maple-dark mb-6">
                                        ORDER SUMMARY
                                    </h2>

                                    <div className="mb-6 space-y-3">
                                        <label className="flex items-center gap-3 text-maple-dark/70 cursor-pointer">
                                            <input type="radio" name="fulfillment" value="ship" checked={fulfillmentMethod === 'ship'} onChange={() => setFulfillmentMethod('ship')} />
                                            <span>Ship my order</span>
                                        </label>
                                        <label className="flex items-center gap-3 text-maple-dark/70 cursor-pointer">
                                            <input type="radio" name="fulfillment" value="local_pickup" checked={fulfillmentMethod === 'local_pickup'} onChange={() => setFulfillmentMethod('local_pickup')} />
                                            <span>Local pickup (free)</span>
                                        </label>
                                    </div>

                                    <div className="space-y-4 border-b border-black/10 pb-6">
                                        <div className="flex justify-between text-maple-dark/60">
                                            <span>Subtotal</span>
                                            <span className="font-mono">${total.toFixed(2)}</span>
                                        </div>
                                        {promoApplied && (
                                            <div className="flex justify-between text-green-600" data-testid="promo-discount">
                                                <span className="flex items-center gap-2">
                                                    Discount ({promoApplied.code})
                                                    <button onClick={removePromo} className="text-maple-red hover:text-red-700">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </span>
                                                <span className="font-mono">-${promoApplied.discount_amount.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between text-maple-dark/60 gap-4">
                                            <span>Shipping</span>
                                            <span className="font-mono text-right">{shippingPreviewText}</span>
                                        </div>
                                    </div>

                                    {/* Promo Code Input */}
                                    {!promoApplied && (
                                        <div className="mt-4 mb-4" data-testid="promo-code-section">
                                            <label className="text-maple-dark/60 text-xs font-mono uppercase tracking-wider block mb-2">Promo Code</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={promoCode}
                                                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                                                    placeholder="ENTER CODE"
                                                    className="flex-1 bg-white border border-black/10 px-3 py-2 font-mono text-sm text-maple-dark placeholder:text-black/30 uppercase"
                                                    data-testid="promo-code-input"
                                                />
                                                <button
                                                    onClick={handleApplyPromo}
                                                    disabled={applyingPromo || !promoCode.trim()}
                                                    className="px-4 py-2 bg-maple-dark text-white text-xs font-manrope uppercase tracking-wider hover:bg-maple-red transition-colors disabled:opacity-50"
                                                    data-testid="apply-promo-btn"
                                                >
                                                    {applyingPromo ? '...' : 'Apply'}
                                                </button>
                                            </div>
                                            {promoError && <p className="text-red-500 text-xs mt-1" data-testid="promo-error">{promoError}</p>}
                                        </div>
                                    )}

                                    <div className="flex justify-between text-maple-dark text-lg mt-6">
                                        <span className="font-anton">TOTAL</span>
                                        <span className="font-mono" data-testid="cart-total">${displayTotal.toFixed(2)}</span>
                                    </div>

                                    <button
                                        onClick={handleCheckout}
                                        disabled={isCheckingOut}
                                        className="btn-primary w-full mt-8 flex items-center justify-center gap-2 disabled:opacity-50"
                                        data-testid="checkout-button"
                                    >
                                        <span>{isCheckingOut ? 'Processing...' : 'Checkout'}</span>
                                        {!isCheckingOut && <ArrowRight className="w-4 h-4" style={{ transform: 'skewX(6deg)' }} />}
                                    </button>

                                    <Link 
                                        to="/shop" 
                                        className="block text-center text-maple-dark/40 hover:text-maple-dark text-sm mt-4 transition-colors"
                                    >
                                        Continue Shopping
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default CartPage;